import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { Grid as InavistaGrid, GridConfig, GridHyperlinkClickEvent } from '@inavista/components/grid';
import { ApiResponse, UserRoles } from 'projects/service/src/app/shared/models/app.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { CommonService } from '../../../../shared/services/common.service';
import { ServiceOrderService } from '../../services/service-order.service';
import {
    buildEquipmentHistoryGridConfig, buildSoHistoryGridConfig, EQUIPMENT_HISTORY_COLUMNS, EQUIPMENT_HISTORY_URL,
    SO_HISTORY_COLUMNS, SO_HISTORY_URL,
} from './history.grid.config';

@Component({
    selector: 'app-history-tab',
    templateUrl: './history-tab.component.html',
    styleUrls: ['./history-tab.component.scss'],
    providers: [
        // Both History grids read rows this component holds (the order's history, the equipment history call).
        ...provideLocalGridRows()
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ServiceOrderHistoryTabComponent implements OnInit, OnChanges {
    private destroy$: Subject<null> = new Subject();
    private readonly gridRows = inject(LocalRowsGridApi);

    @Input() serviceOrder = null;

    modelDescription: any;
    serialNumber: any;
    equipmentInfoData: any;

    /** The Service Order grid's config; the grid shows when the order has history, as before. */
    serviceOrderGridConfig: GridConfig = null;
    /** The Equipment grid's config; set once the equipment history call answered (success or not), as before. */
    equipmentGridConfig: GridConfig = null;
    private equipmentRows: any[] = [];

    @ViewChild('equipmentsGrid') equipmentsGrid: InavistaGrid;
    @ViewChild('serviceOrdersGrid') serviceOrdersGrid: InavistaGrid;
    showPopup: boolean = false;
    showLoader: boolean = false;
    serviceOrderData: any = {};
    serviceOrderID: any = null;
    isIsc: boolean = false;
    isIscManager: boolean = false;
    isIscSupervisor: boolean;
    private readonly canExport: boolean;

    constructor(
        private commonService: CommonService,
        private serviceOrderService: ServiceOrderService
    ) { 
        this.isIsc = commonService.USER.role === UserRoles.isc;
        this.isIscManager = commonService.USER.role === UserRoles.iscManager;
        this.isIscSupervisor = commonService.USER.role === UserRoles.IscSupervisor;
        // Excel export showed on both grids for this role claim only, as before.
        this.canExport = !!this.commonService.roleClaims['AllGrid_Toolbar_Excel_Export']?.visible;
    }

    ngOnInit(): void {
        if ( this.serviceOrder?.serviceOrderId ) { this.getEquipmentHistory(); }
        // Kept as it was: 'CreatedDate' (capital C) is not the field the grid shows ('createdDate').
        this.serviceOrder?.history?.map(item => {
            item.CreatedDate = new Date(item.CreatedDate);
        });

        this.serviceOrderService.modelDescriptionChange.subscribe(res => {
            this.modelDescription = res;
        });

        this.serviceOrderService.serialNumberChange.pipe(
            takeUntil(this.destroy$),
        ).subscribe((args: any) => {
            this.serialNumber = args.itemData ? args.itemData.serialNumber : null;
        });

        this.serviceOrderService.equipmentInfoSubject.pipe(
            takeUntil(this.destroy$),
        ).subscribe(equipmentInfo => {
            this.equipmentInfoData = equipmentInfo;
            // this.modelDescription = equipmentInfo.description;
            // this.serialNumber = equipmentInfo.value;
        });

        this.serviceOrderGridConfig = buildSoHistoryGridConfig(this.canExport);
        this.refreshServiceOrderGrid();
    }

    ngOnChanges(change: SimpleChanges) {
        if (change && change.serviceOrder) {
            this.serviceOrder = change.serviceOrder.currentValue;
            this.refreshServiceOrderGrid();
        }
    }

    get hasHistory(): boolean {
        return !!this.serviceOrder?.history?.length;
    }

    // The Service Order grid's rows: the order's history array, as it is.
    refreshServiceOrderGrid() {
        const history: any[] = this.serviceOrder?.history ?? [];
        this.gridRows.set(SO_HISTORY_URL, LocalRowsGridApi.schema(SO_HISTORY_COLUMNS), history.map((h, i) => ({ ...h, rowId: i })));
        this.serviceOrdersGrid?.refresh();
    }

    // The Equipment grid's rows: the last answer of the equipment history call.
    refreshEquipmentGrid() {
        this.gridRows.set(EQUIPMENT_HISTORY_URL, LocalRowsGridApi.schema(EQUIPMENT_HISTORY_COLUMNS), this.equipmentRows.map((r, i) => ({ ...r, rowId: i })));
        if (this.equipmentGridConfig) {
            this.equipmentsGrid?.refresh();
        } else {
            this.equipmentGridConfig = buildEquipmentHistoryGridConfig(this.canExport);
        }
    }

    getEquipmentHistory() {
        this.serviceOrderService.getEquipmentHistory(
            this.serviceOrder.serviceOrderId,
            this.serviceOrder.itemCode,
            this.serviceOrder.serialNumber,
            this.isIsc || this.isIscManager || this.isIscSupervisor
        ).subscribe( (res: ApiResponse) => {
            if ( res.isSuccessful ) {
                this.equipmentRows = res.result ?? [];
            }
            this.refreshEquipmentGrid();
        }, (error: HttpErrorResponse) => {
            this.refreshEquipmentGrid();
        });
    }

    onEquipmentHyperlinkClick(event: GridHyperlinkClickEvent) {
        if (event.columnId === 'serviceOrderId') {
            this.onServiceOrderClick(event.row['serviceOrderId']);
        }
    }

    onServiceOrderHistoryExport() {
        exportInavistaGrid(this.serviceOrdersGrid, 'Service Orders History');
    }

    onEquipmentHistoryExport() {
        exportInavistaGrid(this.equipmentsGrid, 'Equipment History');
    }

    onServiceOrderClick(serviceOrderID) {
        this.serviceOrderService.getServiceOrderById(serviceOrderID)
            .subscribe((res: any) => {
                this.serviceOrderID = serviceOrderID;
                this.serviceOrderData = res.result;
                this.showLoader = false;
                this.showPopup = true;
            }, (error: HttpErrorResponse) => {
                this.showLoader = false;
                throw error;
            });
    }

    ngOnDestroy() {
        this.destroy$.next(null);
        this.destroy$.unsubscribe();
    }

}
